import {useCallback, useEffect, useState} from 'react';

export type AudibleCategory =
  | 'amenity'
  | 'application'
  | 'application_message'
  | 'community_event'
  | 'community_product'
  | 'community_update'
  | 'conversation_assignment'
  | 'team_assignment'
  | 'conversation'
  | 'survey'
  | 'task_assignment'
  | 'task_update'
  | 'task_comment';

const ALL_CATEGORIES: AudibleCategory[] = [
  'amenity',
  'application',
  'application_message',
  'community_event',
  'community_product',
  'community_update',
  'conversation_assignment',
  'team_assignment',
  'conversation',
  'survey',
  'task_assignment',
  'task_update',
  'task_comment',
];

export type AudiblePrefs = {
  master: boolean;
  categories: Record<AudibleCategory, boolean>;
};

const STORAGE_KEY = 'ark_audible_notification_prefs';
const OLD_STORAGE_KEY = 'ark_audible_notification_enabled';

const defaultPrefs = (): AudiblePrefs => ({
  master: false,
  categories: Object.fromEntries(ALL_CATEGORIES.map(c => [c, true])) as Record<
    AudibleCategory,
    boolean
  >,
});

const readPrefs = (): AudiblePrefs => {
  const old = localStorage.getItem(OLD_STORAGE_KEY);
  if (old !== null) {
    localStorage.removeItem(OLD_STORAGE_KEY);
    const migrated: AudiblePrefs = {
      ...defaultPrefs(),
      master: old === 'true',
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
    return migrated;
  }

  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return defaultPrefs();
  try {
    return JSON.parse(raw) as AudiblePrefs;
  } catch {
    return defaultPrefs();
  }
};

const writePrefs = (prefs: AudiblePrefs) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
};

export const getAudibleNotificationEnabled = () => readPrefs().master;

export const getAudibleEnabledForCategory = (
  category: AudibleCategory | undefined,
): boolean => {
  const prefs = readPrefs();
  if (!prefs.master) return false;
  if (!category) return true;
  return prefs.categories[category] ?? true;
};

export const useAudibleNotification = () => {
  const [prefs, setPrefsState] = useState<AudiblePrefs>(defaultPrefs);

  useEffect(() => {
    setPrefsState(readPrefs());
  }, []);

  const setMaster = useCallback((value: boolean) => {
    setPrefsState(prev => {
      const next: AudiblePrefs = {
        master: value,
        categories: prev.categories,
      };
      writePrefs(next);
      return next;
    });
  }, []);

  const setCategoryEnabled = useCallback(
    (category: AudibleCategory, value: boolean) => {
      setPrefsState(prev => {
        const next: AudiblePrefs = {
          ...prev,
          categories: {...prev.categories, [category]: value},
        };
        writePrefs(next);
        return next;
      });
    },
    [],
  );

  return {prefs, setMaster, setCategoryEnabled} as const;
};
